/**
 * Patient photo storage helpers.
 *
 * NOTE: We currently persist per-patient photo variants in localStorage
 * (per-device). If the clinic later wants cross-device sync, this module is
 * the single point of change — swap the `localStorage.*` calls for API calls
 * to a `POST /api/patients/:id/photo` endpoint that stores the WebP blobs to
 * the existing object-storage bucket.
 *
 * We store three variants so that the row-list thumbnail, the profile header,
 * and any full-screen viewer can all use appropriately sized data-URLs
 * without repeatedly decoding the largest image.
 *   - thumb   : 96×96 WebP  (patient list, small cards)
 *   - medium  : 320×320 WebP (profile header, drawers)
 *   - original: 800×800 WebP (viewer, download)
 */
const KEY = (pid, size) => `viyora.patient.${pid}.photo.${size}`;
const LEGACY_KEY = (pid) => `viyora.patient.${pid}.photo`;

export const PHOTO_SIZES = ["thumb", "medium", "original"];

/** Read a photo variant. Falls back to the legacy single-key format so
 * previously-uploaded avatars keep rendering after this refactor. */
export function getPhoto(pid, size = "medium") {
  if (!pid) return null;
  return (
    localStorage.getItem(KEY(pid, size))
    || (size === "medium" ? localStorage.getItem(LEGACY_KEY(pid)) : null)
  );
}

/** Save all three size variants at once (all data-URLs). */
export function setPhotoVariants(pid, variants) {
  if (!pid || !variants) return;
  for (const size of PHOTO_SIZES) {
    if (variants[size]) localStorage.setItem(KEY(pid, size), variants[size]);
  }
  // Clean up legacy single-key value so we don't leak stale data
  localStorage.removeItem(LEGACY_KEY(pid));
  // Broadcast so every mounted <PatientAvatar> in this tab re-renders without
  // needing a page refresh. localStorage's "storage" event only fires across
  // tabs, not within the same one — hence the custom event.
  emitPhotoChanged(pid);
}

/** Remove all size variants for this patient. */
export function removePhoto(pid) {
  if (!pid) return;
  for (const size of PHOTO_SIZES) localStorage.removeItem(KEY(pid, size));
  localStorage.removeItem(LEGACY_KEY(pid));
  emitPhotoChanged(pid);
}

// ---- Reactive pub-sub -------------------------------------------------------
export const PHOTO_EVENT = "viyora:patient-photo-changed";

function emitPhotoChanged(pid) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PHOTO_EVENT, { detail: { patientId: pid } }));
}

/**
 * Render an in-memory <img> onto three progressively-smaller WebP canvases,
 * honouring the user's crop rectangle (cx, cy, size in image-space) and
 * rotation (0/90/180/270°). Returns `{ thumb, medium, original }` data-URLs.
 *
 * This runs entirely client-side, so we never ship the raw megapixel image
 * over the wire — a JPG selfie at 4032×3024 becomes ~40KB WebP × 3 variants.
 */
export async function generatePhotoVariants(img, { cx, cy, size, rotate = 0 }) {
  const targets = { thumb: 96, medium: 320, original: 800 };
  const out = {};
  for (const [key, dim] of Object.entries(targets)) {
    const canvas = document.createElement("canvas");
    canvas.width = dim;
    canvas.height = dim;
    const ctx = canvas.getContext("2d");
    // Paint a clean white background BEFORE the transformed drawImage.
    // When the crop rectangle extends beyond the natural image bounds
    // (which is normal at the "Fit" zoom for non-square photos), the
    // out-of-bounds source pixels are transparent black — filling with
    // white first prevents ugly dark bars around portrait/landscape photos.
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, dim, dim);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.save();
    // Rotate around the canvas centre so the crop rectangle stays anchored.
    ctx.translate(dim / 2, dim / 2);
    ctx.rotate((rotate * Math.PI) / 180);
    // Draw so the crop-rect maps 1:1 to the canvas.
    ctx.drawImage(
      img,
      cx - size / 2, cy - size / 2, size, size,   // src rect
      -dim / 2, -dim / 2, dim, dim,               // dst rect
    );
    ctx.restore();
    // WebP q≈0.85 hits the readability/size sweet-spot for portrait crops.
    out[key] = canvas.toDataURL("image/webp", key === "thumb" ? 0.8 : 0.85);
  }
  return out;
}

/** Load an image from a data-URL / blob-URL and resolve when decoded. */
export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
